import { zql } from "../schema";
import { ADMIN_PLUS } from "../constants";
import type { WorkspaceId, ChannelId, UserId } from "../types/branded-ids";

// ── Auth helpers ─────────────────────────────────────────────────────────────

export const ROLE_RANK: Record<string, number> = {
  primary_owner: 5,
  owner: 4,
  admin: 3,
  member: 2,
  collaborator: 1,
  multi_channel_guest: 1,
  single_channel_guest: 0,
};

export async function requireWorkspaceRole(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tx: { run: (query: any) => Promise<any> },
  workspaceId: WorkspaceId,
  sub: UserId,
  allowedRoles: readonly string[] = ADMIN_PLUS,
) {
  const m = await tx.run(
    zql.workspace_members
      .where("workspace_id", workspaceId)
      .where("user_id", sub)
      .where("removed_at", "IS", null)
      .one(),
  );
  if (!m || !allowedRoles.includes(m.role)) {
    throw new Error("Unauthorized: insufficient workspace role");
  }
  return m;
}

/**
 * Membership-active check for write-side mutator auth. The PushProcessor
 * runs mutators server-side against full Postgres (not the
 * publication-filtered replica), so a tombstoned `channel_members` row
 * still satisfies a plain `user_id = sub` predicate and would let a
 * kicked user pass membership checks. Use this helper anywhere a
 * mutator needs to confirm "sub is an active channel member." Returns
 * the active membership row or null.
 */
export async function getActiveChannelMembership(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tx: { run: (query: any) => Promise<any> },
  channelId: ChannelId,
  sub: UserId,
) {
  return await tx.run(
    zql.channel_members
      .where("channel_id", channelId)
      .where("user_id", sub)
      .where("removed_at", "IS", null)
      .one(),
  );
}

// ── Utilities ────────────────────────────────────────────────────────────────

export const generateId = () => crypto.randomUUID();
export const normalizeMemberIds = (memberUserIds: string[]) =>
  [...new Set(memberUserIds)].sort();
export const normalizeWorkspaceSlug = (slug: string) =>
  slug.trim().toLowerCase();

const workspaceSlugPattern = /^[a-z0-9-]+$/;
const reservedWorkspaceSlugs = new Set([
  "api",
  "api-eu",
  "api-staging",
  "api-us",
  "app",
  "app-staging",
  "beta",
  "home",
  "staging",
  "sync",
  "sync-eu",
  "sync-staging",
  "www",
  // Path-route words: a workspace with one of these slugs would have an
  // unroutable `/<slug>` URL (the SPA + crawler treat them as app routes).
  "c",
  "callback",
  "call-window",
  "integrations",
]);

export function assertWorkspaceSlugValid(normalizedSlug: string) {
  if (!normalizedSlug) {
    throw new Error("Workspace slug cannot be empty");
  }
  if (normalizedSlug.length > 63) {
    throw new Error("Workspace slug must be 63 characters or less");
  }
  if (
    normalizedSlug.startsWith("-") ||
    normalizedSlug.endsWith("-") ||
    !workspaceSlugPattern.test(normalizedSlug)
  ) {
    throw new Error(
      "Workspace slug must contain only lowercase letters, numbers, and hyphens",
    );
  }
  if (reservedWorkspaceSlugs.has(normalizedSlug)) {
    throw new Error("Workspace slug is reserved");
  }
}

export function assertWorkspaceSlugAvailable(
  workspaces: Array<{ id: string; slug: string }>,
  slug: string,
  currentWorkspaceId?: string,
) {
  const normalizedSlug = normalizeWorkspaceSlug(slug);
  assertWorkspaceSlugValid(normalizedSlug);

  const conflict = workspaces.some(
    (workspace) =>
      workspace.id !== currentWorkspaceId &&
      normalizeWorkspaceSlug(workspace.slug) === normalizedSlug,
  );
  if (conflict) {
    throw new Error("Workspace slug is already taken");
  }
}
